// src/pages/PrintHabitacionesPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { getToken } from "../services/authService";
import "./print-habitaciones.css";
import logo from "../assets/logo-melonares.png";
import { useLocation, useSearchParams } from "react-router-dom";
// Normaliza strings
const s = (v) => String(v ?? "").trim();



// Intenta extraer nombre de una “persona” en distintos formatos
function personName(p) {
    if (!p) return "";
    if (typeof p === "string") return p.trim();
    if (typeof p === "number") return String(p);
    return (
        s(p.nombreCompleto) ||
        s(p.nombre) ||
        s(p.name) ||
        s(p.fullName) ||
        s(p.apellidos ? `${p.nombre || ""} ${p.apellidos}` : "") ||
        s(p.email) ||
        ""
    );
}

// Extrae huéspedes (máximo 2) y devuelve "A / B"
function guestsLabel(room) {
    const arr =
        (Array.isArray(room?.huespedes) && room.huespedes) ||
        (Array.isArray(room?.huespedesAsignados) && room.huespedesAsignados) ||
        (Array.isArray(room?.participantes) && room.participantes) ||
        (Array.isArray(room?.personas) && room.personas) ||
        (Array.isArray(room?.ocupantes) && room.ocupantes) ||
        [];

    const names = arr.map(personName).filter(Boolean).slice(0, 2);
    return names.join(" / ");
}

// Número de habitación
function roomNumber(room) {
    return (
        s(room?.numeroHabitacion) ||
        s(room?.numero) ||
        s(room?.habitacionNumero) ||
        s(room?.roomNumber) ||
        s(room?.name) ||
        s(room?.id) ||
        ""
    );
}

// Número de personas
function peopleCount(room) {
    const explicit =
        room?.numeroPersonas ??
        room?.numPersonas ??
        room?.personasCount ??
        room?.ocupantesCount ??
        room?.pax;

    if (Number.isFinite(explicit)) return explicit;

    // fallback: si hay array de huéspedes/personas, cuenta eso
    const arr =
        (Array.isArray(room?.huespedes) && room.huespedes) ||
        (Array.isArray(room?.huespedesAsignados) && room.huespedesAsignados) ||
        (Array.isArray(room?.participantes) && room.participantes) ||
        (Array.isArray(room?.personas) && room.personas) ||
        (Array.isArray(room?.ocupantes) && room.ocupantes) ||
        null;

    if (arr) return arr.length;

    const n = Number(room?.numeroPersonas || room?.numPersonas || room?.pax || 0);
    return Number.isFinite(n) ? n : 0;
}

// “Ocupada” si hay huéspedes, o pax > 0, o un flag/estado típico
function isOccupied(room) {
    const guests = guestsLabel(room);
    const pax = peopleCount(room);
    const estado = s(room?.estado || room?.status).toLowerCase();
    const ocupadaFlag = room?.ocupada ?? room?.isOccupied ?? room?.occupied;

    if (ocupadaFlag === true) return true;
    if (estado.includes("ocup")) return true;
    if (guests) return true;
    if (pax > 0) return true;
    return false;
}

// Intenta localizar el array de habitaciones dentro del evento
function pickRoomsFromEvento(evento) {
    const candidates = [
        evento?.habitaciones,
        evento?.rooms,
        evento?.habitacionesEvento,
        evento?.habitacionesAsignadas,
        evento?.asignacionHabitaciones,
        evento?.roomAssignments,
    ].filter(Array.isArray);

    if (candidates.length) return candidates[0];
    return [];
}

export default function PrintHabitacionesPage() {

    const [params] = useSearchParams();
    const key = params.get("k");

    const eventId = params.get("eventId");
    const location = useLocation();
    const preRows = useMemo(() => {
        // 1) si por algún motivo viene por state, lo aprovechamos
        const fromState = location.state?.rows;
        if (Array.isArray(fromState) && fromState.length) return fromState;

        // 2) ruta robusta: sessionStorage vía key en URL
        if (key) {
            try {
                const raw = sessionStorage.getItem(key);
                const parsed = raw ? JSON.parse(raw) : null;
                if (Array.isArray(parsed) && parsed.length) return parsed;
            } catch {
                // ignore
            }
        }

        return null;
    }, [location.state, key]);


    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [evento, setEvento] = useState(null);

    const [assetsReady, setAssetsReady] = useState(false);
    const [logoFailed, setLogoFailed] = useState(false);

    useEffect(() => {
        console.log("PRINT preRows:", preRows);
        console.log("PRINT eventId:", eventId);
    }, [preRows, eventId]);

    useEffect(() => {
        let cancelled = false;

        if (Array.isArray(preRows) && preRows.length) {
            setLoading(false);
            setError("");
            return;
        }

        async function load() {
            setLoading(true);
            setError("");

            try {
                const headers = { Authorization: `Bearer ${getToken()}` };

                let res = await fetch(`${process.env.REACT_APP_API_URL}/api/eventos/${eventId}`, { headers });
                if (!res.ok) {
                    const allRes = await fetch(`${process.env.REACT_APP_API_URL}/api/eventos/all`, { headers });
                    const all = await allRes.json();
                    const ev = (all || []).find((e) => String(e.id) === String(eventId));
                    if (!cancelled) setEvento(ev || null);
                } else {
                    const ev = await res.json();
                    if (!cancelled) setEvento(ev);
                }
            } catch (e) {
                if (!cancelled) setError("Error cargando datos");
            } finally {
                if (!cancelled) setLoading(false);
            }
        }

        if (eventId) load();
        else {
            setError("Falta eventId");
            setLoading(false);
        }

        return () => {
            cancelled = true;
        };
    }, [eventId, preRows]);

    // Espera imágenes dentro de .sheet antes de imprimir
    useEffect(() => {
        if (loading || error) return;
        const sheet = document.querySelector(".sheet");
        if (!sheet) return;

        const imgs = Array.from(sheet.querySelectorAll("img"));
        if (imgs.length === 0) {
            setAssetsReady(true);
            return;
        }

        let pending = imgs.length;
        const done = () => {
            pending -= 1;
            if (pending <= 0) setAssetsReady(true);
        };

        imgs.forEach((img) => {
            if (img.complete && img.naturalWidth > 0) {
                done();
            } else {
                img.addEventListener("load", done, { once: true });
                img.addEventListener("error", done, { once: true });
            }
        });

        return () => {
            imgs.forEach((img) => {
                img.onload = null;
                img.onerror = null;
            });
        };
    }, [loading, error, eventId]);

    // Imprime cuando todo está listo
    useEffect(() => {
        if (!loading && !error && assetsReady) {
            const t = setTimeout(() => window.print(), 120);
            const after = () => window.close?.();
            window.addEventListener("afterprint", after, { once: true });
            return () => {
                clearTimeout(t);
                window.removeEventListener("afterprint", after);
            };
        }
    }, [loading, error, assetsReady]);

    const rooms = useMemo(() => {
        // Caso A: viene del state
        if (Array.isArray(preRows) && preRows.length) {
            return preRows
                .map((r) => {
                    const guests = (r.ocupantes || [])
                        .slice(0, 2)
                        .map((o) => o.label)
                        .filter(Boolean)
                        .join(" / ");
                    const pax = (r.ocupantes || []).length;
                    return { num: r.nombre, guests, pax };
                })
                .filter((x) => x.pax > 0);
        }

        // Caso B: fallback
        if (!evento) return [];

        const all = pickRoomsFromEvento(evento);
        return all
            .map((r) => ({
                _raw: r,
                num: roomNumber(r),
                guests: guestsLabel(r),
                pax: peopleCount(r),
            }))
            .filter((r) => isOccupied(r._raw));
    }, [evento, preRows]);


    const BRAND = { name: "LOS MELONARES", monogram: "M" };

    if (loading)
        return (
            <div className="print-wrap luxe">
                <div className="no-print preloader">Cargando…</div>
            </div>
        );

    if (error)
        return (
            <div className="print-wrap luxe">
                <div className="no-print preloader danger">{error}</div>
            </div>
        );

    return (
        <div className="print-wrap luxe">
            <div className="sheet frame">
                {/* Cabecera marca (idéntica) */}
                <header className="brand">
                    {logoFailed ? (
                        <>
                            <div className="monogram">{BRAND.monogram}</div>
                            <div className="brand-name">{BRAND.name}</div>
                        </>
                    ) : (
                        <img
                            className="brand-logo"
                            alt="Los Melonares"
                            src={logo}
                            onError={() => setLogoFailed(true)}
                            style={{ width: "40mm", height: "auto", display: "block" }}
                        />
                    )}
                </header>

                <main className="sheet-body">
                    <div className="rooms-main">

                        {/* Tabla 3 columnas */}
                        <section className="rooms-table" aria-label="Listado de habitaciones ocupadas">
                            <div className="rooms-head">
                                <div className="col col-room">Hab.</div>
                                <div className="col col-guests" aria-hidden="true">&nbsp;</div>
                                <div className="col col-pax">Total</div>
                            </div>

                            <div className="rooms-body">
                                {rooms.length ? (
                                    rooms.map((r, idx) => (
                                        <div className="rooms-row" key={`${r.num}-${idx}`}>
                                            <div className="col col-room">{r.num || "-"}</div>
                                            <div className="col col-guests">{r.guests || "-"}</div>
                                            <div className="col col-pax">{String(r.pax ?? 0)}</div>
                                        </div>
                                    ))
                                ) : (
                                    <div className="rooms-empty">No hay habitaciones ocupadas.</div>
                                )}
                            </div>
                        </section>
                    </div>
                </main>
            </div>
        </div>
    );
}
